import type { FormEventHandler } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { formToObject } from 'lib/forms/formToObject'
import { forgotPassword, login } from 'actions/AuthActions'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import BodyText from 'LuxKit/Typography/BodyText'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { connect } from 'react-redux'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import noop from 'lib/function/noop'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
interface FormData {
  email: string
  password: string
}

interface MappedStateProps {
  authError?: App.ApiCallFailure
  isAuthProcessing?: boolean
}

interface Props {
  user: App.User
  onModeChange?: (mode: App.UiAccountModalMode) => void
}

function AgentHubPasswordLoginForm(props: Props & MappedStateProps) {
  const {
    authError,
    isAuthProcessing,
    user,
    onModeChange = noop,
  } = props

  const intl = useIntl()
  const appDispatch = useAppDispatch()
  const modalContext = useModalElementContext<AccountAccessModalResult>()

  const loggedIn = useAppSelector(selectLoggedIn)

  const formRef = useRef<HTMLFormElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>(async(event) => {
    event.preventDefault()
    const formData = formToObject<FormData>(event.currentTarget)
    const recaptchaResponse = await recaptchaRef.current?.executeAsync()
    if (recaptchaResponse) {
      appDispatch(login({ email: formData.email }, formData.password, recaptchaResponse))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleForgotPassword = useCallback(() => {
    formRef.current?.reset()
    onModeChange('forgotPassword')
    appDispatch(forgotPassword(user?.email || ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    passwordInputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  const onBack = useCallback(() => {
    onModeChange('login')
  }, [onModeChange])

  return <>
    <ModalHeader
      title={intl.formatMessage({
        id: 'lQza7dPBJSpshRgVhca_',
        defaultMessage: 'Welcome back',
        description: 'Title for the agent hub password login modal in the agent portal',
      })}
      onBackButtonClick={onBack}
    />
    <ModalBody>
      <ModalContent>
        <form
          ref={formRef}
          name="agentHubPasswordLoginForm"
          data-testid="agent-hub-password-login-form"
          onSubmit={handleSubmission}
        >
          <Group direction="vertical" gap={20}>
            {!!authError?.message && <MessageBanner kind="critical">{authError.message}</MessageBanner>}

            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'GCCO1xvMg6WIzA_7DaQI',
                defaultMessage: 'Please enter your password below to continue.',
                description: 'Agent Hub password login form instructional body text',
              })}
            </BodyText>

            <Group direction="vertical">
              <HiddenInput
                name="email"
                value={user.email}
              />
              <PasswordInput
                label={intl.formatMessage({
                  id: 'ghuu_eYjTMNDm_HSGEHv',
                  defaultMessage: 'Password',
                  description: 'Agent Hub password input label in the travel agent hub portal',
                })}
                required
                name="password"
                placeholder={intl.formatMessage({
                  id: 'hpsS3cfmdeymTFiDLRqv',
                  defaultMessage: 'Password',
                  description: 'Agent Hub password input placeholder in the travel agent hub portal',
                })}
                requiredErrorMessage={intl.formatMessage({
                  id: 'N_u66Mtn7oC5vW-M4ni4',
                  defaultMessage: 'Password is required',
                  description: 'Validation error shown when the travel agent submits the Agent Hub login form without entering a password',
                })}
                ref={passwordInputRef}
              />

              <Group direction="vertical" gap={16}>
                <TextButton
                  kind="primary"
                  size="large"
                  type="submit"
                  disabled={isAuthProcessing}
                >
                  {intl.formatMessage({
                    id: 'BVSmDupKOFUhlGAPN1kO',
                    defaultMessage: 'Log in',
                    description: 'Agent Hub password login form submit button in the agent portal',
                  })}
                </TextButton>

                <Group direction="horizontal" horizontalAlign="center">
                  <TextLink
                    size="medium"
                    onClick={handleForgotPassword}
                  >
                    {intl.formatMessage({
                      id: 'Ys5WXugCcVFgDzwbfCay',
                      defaultMessage: 'Forgot your password?',
                      description: 'Agent Hub forgot password link text in the travel agent hub portal',
                    })}
                  </TextLink>
                </Group>
              </Group>
            </Group>
          </Group>
          {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={recaptchaRef} />}
        </form>
      </ModalContent>
    </ModalBody>
  </>
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    authError: state.auth.error,
    isAuthProcessing: state.auth.processing,
  }
})(AgentHubPasswordLoginForm)
